import React, { useState, useEffect } from "react";
import T from '../../../components/common/T';
import { usePurchaseOrder } from "../../../context/PurchaseOrderContext";
import { useInventory } from "../../../context/InventoryContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify"; // 🟢 Ensure Container is imported
import "react-toastify/dist/ReactToastify.css";

import OdooFormLayout from "../../../components/OdooFormLayout";
import SmartButton from "../../../components/SmartButton";
import { DocumentTextIcon, BookOpenIcon, TrashIcon } from "@heroicons/react/24/outline";

import { toInputDate } from "../../../utils/dateUtils";

export default function CreatePurchaseRequest() {
  const { id } = useParams(); 
  const isEditMode = Boolean(id && id !== 'new');

  const { createPurchaseRequest, updatePR, fetchPRById, purchaseRequests, updatePRStatus, purchaseOrders } = usePurchaseOrder();
  const { inventories } = useInventory();
  const navigate = useNavigate();
  const loadedIdRef = React.useRef(null);

  const [formData, setFormData] = useState({
    prNumber: "Draft",
    requester: "Admin User",
    requiredBy: new Date().toISOString().split('T')[0],
    notes: "",
    status: "NEW", 
    lines: [{ id: Date.now(), materialId: "", quantity: 1, description: "", unit: "Units" }]
  });

  useEffect(() => {
    if (isEditMode && id) {
      if (loadedIdRef.current === String(id)) return;
      loadedIdRef.current = String(id);

      const loadPR = async () => {
        let found = null;
        if (fetchPRById) {
          found = await fetchPRById(id);
        }
        if (!found && purchaseRequests) {
          found = purchaseRequests.find(pr => String(pr.id) === String(id));
        }
        if (found) {
          setFormData({
            ...found,
            requiredBy: toInputDate(found.requiredBy || found.requestDate),
            lines: (found.lines && found.lines.length > 0)
              ? found.lines 
              : [{ id: Date.now(), materialId: found.materialId, quantity: found.quantity, description: found.materialName, unit: "Units" }]
          });
        }
      };
      loadPR();
    }
  }, [id, isEditMode, fetchPRById, purchaseRequests]);

  const STAGES = ["NEW", "WAITING DEPARTMENT APPROVAL", "WAITING IR APPROVAL", "APPROVED", "PURCHASE ORDER CREATED", "RECEIVED"];

  // --- ACTIONS (🟢 FIXED: Added async/await for Toasts) ---
  
  const handleConfirm = async () => {
      if (!isEditMode) {
          try {
              const newItem = await createPurchaseRequest({
                ...formData,
                materialId: formData.lines[0].materialId,
                quantity: formData.lines[0].quantity,
                status: "WAITING DEPARTMENT APPROVAL" 
              });
              
              toast.success("Request Confirmed! (To Inventory)");
              // Small delay to let toast be seen if needed, or navigate immediately
              navigate(`/admin/material/purchase/edit/${newItem.id}`);
          } catch (e) {
              // Toast handled in context
          }
      } else {
          await updatePRStatus(id, "WAITING DEPARTMENT APPROVAL");
          setFormData(prev => ({ ...prev, status: "WAITING DEPARTMENT APPROVAL" }));
          toast.success("Confirmed! Waiting for Dept Approval.");
      }
  };

  const handleDeptApprove = async () => { 
      await updatePRStatus(id, "WAITING IR APPROVAL"); 
      setFormData(prev => ({ ...prev, status: "WAITING IR APPROVAL" })); 
      toast.success("Department Approved."); 
  };

  const handleIRApprove = async () => { 
      await updatePRStatus(id, "APPROVED"); 
      setFormData(prev => ({ ...prev, status: "APPROVED" })); 
      toast.success("Final Approval Granted."); 
  };

  const handleCreateRFQ = () => { 
      navigate(`/admin/material/purchase/${id}/quotations`); 
  };

  const linkedPOs = purchaseOrders.filter(po => String(po.prId) === String(id));  
  const handleOpenPOs = () => {
      if (linkedPOs.length === 1) navigate(`/admin/material/purchase-orders/${linkedPOs[0].id}`);
      else navigate(`/admin/material/purchase-orders`); 
  };

  // --- HANDLERS ---
  const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateLine = (idx, field, value) => {
      const newLines = [...formData.lines];
      newLines[idx][field] = value;
      if(field === 'materialId') {
          const m = inventories.find(i => String(i.id) === String(value));
          newLines[idx].description = m ? m.itemName : "";
          newLines[idx].unit = m ? m.unit : "Units";
      }
      setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => setFormData(prev => ({ ...prev, lines: [...prev.lines, { id: Date.now(), materialId: "", quantity: 1, unit: "Units" }] }));
  const removeLine = (idx) => setFormData(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const isReadOnly = formData.status !== "NEW" && formData.status !== "Draft";

  const ActionButtons = (
    <>
      {(formData.status === "NEW" || formData.status === "Draft") && <button onClick={handleConfirm} className="bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-purple-800 transition shadow-sm"><T>Confirm</T></button>}
      {formData.status === "WAITING DEPARTMENT APPROVAL" && <button onClick={handleDeptApprove} className="bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-purple-800 transition shadow-sm"><T>Department Approve</T></button>}
      {formData.status === "WAITING IR APPROVAL" && <button onClick={handleIRApprove} className="bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-purple-800 transition shadow-sm"><T>Approve</T></button>}
      {formData.status === "APPROVED" && <button onClick={handleCreateRFQ} className="bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-purple-800 transition shadow-sm"><T>Create PO</T></button>}
      <button onClick={() => navigate("/admin/material/purchase")} className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-xs font-bold uppercase hover:bg-gray-50 transition ml-2"><T>Cancel</T></button>
    </>
  );

  const SmartButtons = (
    <>
      {(formData.status === "PURCHASE ORDER CREATED" || formData.status === "RECEIVED") && <SmartButton label="Purchase Orders" value={linkedPOs.length} icon={BookOpenIcon} onClick={handleOpenPOs} />}
      <SmartButton label="Documents" value="0" icon={DocumentTextIcon} />
    </>
  );

  return (
    <>
      {/* 🟢 Add ToastContainer here so alerts work */}
      
      <OdooFormLayout
        title={formData.prNumber || "New Request"}
        subTitle={"Stock Purchase Request"}
        status={formData.status}
        stages={STAGES}
        actions={ActionButtons}
        smartButtons={SmartButtons}
      >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4 mb-8">
              <div className="space-y-4">
                  <div className="flex items-center">
                      <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Requester</T></label>
                      <input name="requester" value={formData.requester} onChange={handleChange} disabled={isReadOnly} className="flex-1 border-b border-gray-300 py-1 focus:border-purple-600 outline-none bg-transparent" />
                  </div>
              </div>
              <div className="space-y-4">
                  <div className="flex items-center">
                      <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Request Date</T></label>
                      <input type="date" name="requiredBy" value={formData.requiredBy} onChange={handleChange} disabled={isReadOnly} className="flex-1 border-b border-gray-300 py-1 focus:border-purple-600 outline-none bg-transparent" />
                  </div>
              </div>
          </div>

          <div className="mt-8">
              <div className="border-b border-gray-200 mb-4 flex gap-6">
                  <button className="text-purple-700 border-b-2 border-purple-700 pb-2 text-xs font-bold uppercase"><T>Products</T></button>
                  <button className="text-gray-500 hover:text-gray-700 pb-2 text-xs font-bold uppercase"><T>Other Information</T></button>
              </div>

              <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                      <tr>
                          <th className="px-3 py-2"><T>Product</T></th>
                          <th className="px-3 py-2"><T>Description</T></th>
                          <th className="px-3 py-2 text-right"><T>Quantity</T></th>
                          <th className="px-3 py-2"><T>Unit</T></th>
                          <th className="px-3 py-2 w-10"></th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {formData.lines.map((line, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                  <select value={line.materialId} onChange={e => updateLine(idx, 'materialId', e.target.value)} disabled={isReadOnly} className="w-full bg-transparent outline-none">
                                      <option value=""><T>Select Product...</T></option>
                                      {inventories.map(i => <option key={i.id} value={i.id}>{i.itemName}</option>)}
                                  </select>
                              </td>
                              <td className="px-3 py-2"><input value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)} disabled={isReadOnly} className="w-full bg-transparent outline-none" /></td>
                              <td className="px-3 py-2 text-right"><input type="number" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} disabled={isReadOnly} className="w-20 bg-transparent outline-none text-right" /></td>
                              <td className="px-3 py-2 text-gray-500">{line.unit}</td>
                              <td className="px-3 py-2 text-center">{!isReadOnly && <button onClick={() => removeLine(idx)} className="text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
              {!isReadOnly && <button onClick={addLine} className="mt-4 text-purple-700 text-xs font-bold uppercase hover:underline"><T>Add a line</T></button>}
          </div>
      </OdooFormLayout>
    </>
  );
}